import React, { useState, useEffect } from "react";
import axios from "axios";
import { MuiColorInput } from "mui-color-input";
import VideoGenerationStatus from "./VideoGenerationStatus";
import ScheduledJobsManager from "./ScheduledJobsManager";
import LanguageBox from "../../Wayfinder/LanguageBox";
import { usePxToVw } from "../../../hooks/usePxToVw";

const FONT_FAMILIES = [
  { value: "inherit", label: "Inherit (Default)" },
  { value: "Arial", label: "Arial" },
  { value: "Verdana", label: "Verdana" },
  { value: "Times New Roman", label: "Times New Roman" },
  { value: "Courier New", label: "Courier New" },
  { value: "Georgia", label: "Georgia" },
  { value: "GothamMedium", label: "Gotham Medium" },
  { value: "Gothambook", label: "Gotham Book" },
  { value: "playfairdisplay", label: "Playfair Display Regular" },
  { value: "IBMPlexSans-Bold", label: "IBM Plex Sans Bold" },
  { value: "IBMPlexSans-Medium", label: "IBM Plex Sans Medium" },
  { value: "IBMPlexSans-Regular", label: "IBM Plex Sans Regular" },
  { value: "IBMPlexSans-SemiBold", label: "IBM Plex Sans SemiBold" },
  { value: "IBMPlexSans-Thin", label: "IBM Plex Sans Thin" },
  { value: "georgia-normal", label: "Georgia Normal" },
  { value: "brandon-grotesque-bold", label: "Brandon Grotesque Bold" },
  { value: "brandon-grotesque-black", label: "Brandon Grotesque Black" },
  { value: "NeueFrutigerWorld-Black", label: "Neue Frutiger World Black" },
  { value: "NeueFrutigerWorld-Bold", label: "Neue Frutiger World Bold" },
  { value: "NeueFrutigerWorld-Book", label: "Neue Frutiger World Book" },
  { value: "NeueFrutigerWorld-Heavy", label: "Neue Frutiger World Heavy" },
  { value: "NeueFrutigerWorld-It", label: "Neue Frutiger World Italic" },
  { value: "NeueFrutigerWorld-Light", label: "Neue Frutiger World Light" },
  { value: "NeueFrutigerWorld-Medium", label: "Neue Frutiger World Medium" },
  { value: "NeueFrutigerWorld-Regular", label: "Neue Frutiger World Regular" },
  { value: "NeueFrutigerWorld-Thin", label: "Neue Frutiger World Thin" },
  {
    value: "NeueFrutigerWorld-XtraBlk",
    label: "Neue Frutiger World Extra Black",
  },
  { value: "BebasNeue-Regular", label: "Bebas Neue Regular" },
];

const LanguageSelector = ({ userId, buttonColor }) => {
  const [languages, setLanguages] = useState(["en"]);
  const [languageSettings, setLanguageSettings] = useState([]);
  const [targetLanguage, setTargetLanguage] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [languageToDelete, setLanguageToDelete] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });



  const [languageOffsets, setLanguageOffsets] = useState({
    left: 20,
    top: 50,
    right: null,
  });
  const [isSavingOffsets, setIsSavingOffsets] = useState(false);

  const [languageCustomization, setLanguageCustomization] = useState({
    // Outer Box Settings
    outerBackgroundColor: "#808080",
    outerOpacity: 100,
    outerBorderRadius: 15,
    outerBorderWidth: 2,
    outerBorderColor: "#ffffff",
    outerPadding: 15,
    gap: 10,
    outerWidth: null, // null means auto width
    outerHeight: null, // null means auto height
    // Outer Box Shadow
    outerShadowH: 0,
    outerShadowV: 4,
    outerShadowBlur: 20,
    outerShadowSpread: 0,
    outerShadowColor: "#000000",
    outerShadowOpacity: 40,
    // Inner Box Settings
    innerBackgroundColor: "#ffffff",
    innerOpacity: 20,
    innerBorderRadius: 9,
    innerBorderWidth: 2,
    innerBorderColor: "#ffffff",
    innerPadding: 15,
    // Inner Box Shadow
    innerShadowH: 0,
    innerShadowV: 2,
    innerShadowBlur: 8,
    innerShadowSpread: 0,
    innerShadowColor: "#000000",
    innerShadowOpacity: 15,
    // Text Settings
    fontFamily: "inherit",
    fontSize: 18,
    fontWeight: "700",
    textColor: "#ffffff",
    // Icon/Flag Settings
    enableIcon: true,
    flagSize: 60,
  });
  const [isSavingCustomization, setIsSavingCustomization] = useState(false);
  const [languageIcons, setLanguageIcons] = useState({}); // Store custom icons per language
  const [languageIconSizes, setLanguageIconSizes] = useState({}); // Store custom icon sizes per language
  const [languageIconPreviews, setLanguageIconPreviews] = useState({}); // Store preview images per language
  const [pendingIconUploads, setPendingIconUploads] = useState({}); // Store pending file uploads per language
  const [languageDisplayNames, setLanguageDisplayNames] = useState({}); // Store custom display names per language

  const [showVideoScheduleModal, setShowVideoScheduleModal] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState(null);
  const [scheduleOption, setScheduleOption] = useState("now");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [isScheduling, setIsScheduling] = useState(false);
  
  // Regenerate language states
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [languageToRegenerate, setLanguageToRegenerate] = useState(null);
  const [regenerateVideos, setRegenerateVideos] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [scheduledJobs, setScheduledJobs] = useState([]);
  
  const [availableLanguages] = useState([
    { code: "en", name: "English", flag: "us" },
    { code: "es", name: "Spanish", flag: "es" },
    { code: "fr", name: "French", flag: "fr" },
    { code: "de", name: "German", flag: "de" },
    { code: "it", name: "Italian", flag: "it" },
    { code: "pt", name: "Portuguese", flag: "pt" },
    { code: "ru", name: "Russian", flag: "ru" },
    { code: "zh", name: "Chinese", flag: "cn" },
    { code: "ja", name: "Japanese", flag: "jp" },
    { code: "ko", name: "Korean", flag: "kr" },
    { code: "ar", name: "Arabic", flag: "sa" },
    { code: "hi", name: "Hindi", flag: "in" },
    { code: "tr", name: "Turkish", flag: "tr" },
    { code: "nl", name: "Dutch", flag: "nl" },
    { code: "sv", name: "Swedish", flag: "se" },
    { code: "pl", name: "Polish", flag: "pl" },
    { code: "th", name: "Thai", flag: "th" },
    { code: "vi", name: "Vietnamese", flag: "vn" },
    { code: "id", name: "Indonesian", flag: "id" },
  ]);

  const { pxToVw } = usePxToVw();

  useEffect(() => {
    if (userId) {
      fetchLanguages();
      fetchLanguageSettings();
      fetchLanguageOffsets();
      fetchScheduledJobs();
      fetchLanguageCustomization();
    }
  }, [userId]);

  // Prevent page refresh/navigation during generation or regeneration
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isGenerating || isRegenerating) {
        e.preventDefault();
        e.returnValue = 'Language generation is in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isGenerating, isRegenerating]);

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`/api/language/${userId}`);
      if (response.data.success) setLanguages(response.data.languages);
    } catch (error) {
      console.error("Error fetching languages:", error);
      showNotification("error", "Failed to load languages");
    }
  };

  const fetchLanguageSettings = async () => {
    try {
      const response = await axios.get(`/api/language/settings/${userId}`);
      if (response.data.success) {
        setLanguageSettings(response.data.languages);

        // Extract custom icons, sizes, and display names from language settings
        const icons = {};
        const sizes = {};
        const displayNames = {};
        response.data.languages.forEach(lang => {
          if (lang.customIconUrl) {
            icons[lang.code] = lang.customIconUrl;
          }
          if (lang.customIconSize) {
            sizes[lang.code] = lang.customIconSize;
          }
          if (lang.customDisplayName) {
            displayNames[lang.code] = lang.customDisplayName;
          }
        });
        setLanguageIcons(icons);
        setLanguageIconSizes(sizes);
        setLanguageDisplayNames(displayNames);
      }
    } catch (error) {
      console.error("Error fetching language settings:", error);
    }
  };

  const fetchLanguageOffsets = async () => {
    try {
      const response = await axios.get(`/api/language/offsets/${userId}`);
      if (response.data.success && response.data.offsets)
        setLanguageOffsets(response.data.offsets);
    } catch (error) {
      console.error("Error fetching language offsets:", error);
    }
  };

  const fetchLanguageCustomization = async () => {
    try {
      const response = await axios.get(`/api/language/customization/${userId}`);
      if (response.data.success && response.data.customization)
        setLanguageCustomization((prev) => ({
          ...prev,
          ...response.data.customization,
        }));
    } catch (error) {
      console.error("Error fetching language customization:", error);
    }
  };

  const fetchScheduledJobs = async () => {
    try {
      const response = await axios.get(`/api/language/scheduled-jobs/${userId}`);
      if (response.data.success) {
        setScheduledJobs(response.data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching scheduled jobs:', error);
    }
  };

  const saveLanguageOffsets = async () => {
    setIsSavingOffsets(true);
    try {
      const response = await axios.post("/api/language/offsets", {
        userId,
        offsets: languageOffsets,
      });
      if (response.data.success)
        showNotification("success", "Language position saved successfully!");
    } catch (error) {
      showNotification("error", "Failed to save language position");
    } finally {
      setIsSavingOffsets(false);
    }
  };

  const saveLanguageCustomization = async () => {
    setIsSavingCustomization(true);
    try {
      // Save customization settings
      const response = await axios.post("/api/language/customization", {
        userId,
        customization: languageCustomization,
      });

      // Save icon sizes for all languages that have custom icons
      const iconSizePromises = Object.keys(languageIconSizes).map(async (langCode) => {
        if (languageIcons[langCode]) {
          return axios.post("/api/language/language-icon", {
            userId,
            languageCode: langCode,
            customIconUrl: languageIcons[langCode],
            customIconSize: languageIconSizes[langCode],
          });
        }
      });

      await Promise.all(iconSizePromises.filter(Boolean));

      if (response.data.success) {
        showNotification(
          "success",
          "Language customization saved successfully!",
        );
        // Refresh language settings to get updated data
        fetchLanguageSettings();
      }
    } catch (error) {
      showNotification("error", "Failed to save language customization");
    } finally {
      setIsSavingCustomization(false);
    }
  };

  const handleCustomIconUpload = async (e, languageCode) => {
    const file = e.target.files[0];
    if (!file) return;

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);

    // Store preview and pending file
    setLanguageIconPreviews((prev) => ({
      ...prev,
      [languageCode]: previewUrl,
    }));

    setPendingIconUploads((prev) => ({
      ...prev,
      [languageCode]: file,
    }));
  };

  const handleConfirmIconUpload = async (languageCode) => {
    const file = pendingIconUploads[languageCode];
    if (!file) return;

    try {
      // Get signed URL from backend
      const signedUrlResponse = await axios.get("/generate-signed-url", {
        params: {
          fileName: file.name,
          fileType: file.type,
        },
      });

      const { url: signedUrl, publicUrl } = signedUrlResponse.data;

      // Upload file to S3 using signed URL
      await axios.put(signedUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      // Get the icon size for this language (default to 60 if not set)
      const iconSize = languageIconSizes[languageCode] || 60;

      // Update language icon in backend with size
      await axios.post("/api/language/language-icon", {
        userId,
        languageCode,
        customIconUrl: publicUrl,
        customIconSize: iconSize,
      });

      // Update local state
      setLanguageIcons((prev) => ({
        ...prev,
        [languageCode]: publicUrl,
      }));

      // Clear preview and pending upload
      setLanguageIconPreviews((prev) => {
        const newPreviews = { ...prev };
        delete newPreviews[languageCode];
        return newPreviews;
      });

      setPendingIconUploads((prev) => {
        const newUploads = { ...prev };
        delete newUploads[languageCode];
        return newUploads;
      });

      showNotification("success", `Icon uploaded for ${getLanguageName(languageCode)}!`);

      // Refresh language settings
      fetchLanguageSettings();
    } catch (error) {
      console.error("Error uploading icon:", error);
      showNotification("error", "Failed to upload icon");
    }
  };

  const handleCancelIconUpload = (languageCode) => {
    // Revoke preview URL to free memory
    if (languageIconPreviews[languageCode]) {
      URL.revokeObjectURL(languageIconPreviews[languageCode]);
    }

    // Clear preview and pending upload
    setLanguageIconPreviews((prev) => {
      const newPreviews = { ...prev };
      delete newPreviews[languageCode];
      return newPreviews;
    });

    setPendingIconUploads((prev) => {
      const newUploads = { ...prev };
      delete newUploads[languageCode];
      return newUploads;
    });
  };

  const handleIconSizeChange = (languageCode, newSize) => {
    // Only update local state - will be saved when user clicks "Save Customization"
    setLanguageIconSizes((prev) => ({
      ...prev,
      [languageCode]: newSize,
    }));
  };

  const handleRemoveCustomIcon = async (languageCode) => {
    try {
      // Remove icon from backend
      await axios.post("/api/language/language-icon", {
        userId,
        languageCode,
        customIconUrl: null,
      });

      // Update local state
      setLanguageIcons((prev) => {
        const newIcons = { ...prev };
        delete newIcons[languageCode];
        return newIcons;
      });

      showNotification("success", `Icon removed for ${getLanguageName(languageCode)}!`);

      // Refresh language settings
      fetchLanguageSettings();
    } catch (error) {
      console.error("Error removing icon:", error);
      showNotification("error", "Failed to remove icon");
    }
  };

  const handleDisplayNameChange = (languageCode, newDisplayName) => {
    setLanguageDisplayNames((prev) => ({
      ...prev,
      [languageCode]: newDisplayName,
    }));
  };

  const handleSaveDisplayName = async (languageCode) => {
    try {
      const displayName = languageDisplayNames[languageCode] || null;
      
      await axios.post("/api/language/custom-display-name", {
        userId,
        languageCode,
        customDisplayName: displayName,
      });

      showNotification("success", `Display name saved for ${getLanguageName(languageCode)}!`);
      
      // Refresh language settings
      fetchLanguageSettings();
    } catch (error) {
      console.error("Error saving display name:", error);
      showNotification("error", "Failed to save display name");
    }
  };

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(
      () => setNotification({ show: false, type: "", message: "" }),
      3000,
    );
  };

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const filteredLanguages = availableLanguages.filter(
    (lang) =>
      !languages.includes(lang.code) &&
      (lang.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lang.code.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  const handleGenerateLanguage = async () => {
    if (!targetLanguage) {
      showNotification("error", "Please select a target language");
      return;
    }
    if (languages.includes(targetLanguage)) {
      if (
        !window.confirm(
          `Language ${getLanguageName(targetLanguage)} already exists. Regenerate?`,
        )
      )
        return;
    }
    setPendingLanguage(targetLanguage);
    setShowVideoScheduleModal(true);
  };

  const handleGenerateNow = async () => {
    setShowVideoScheduleModal(false);
    setIsGenerating(true);
    try {
      const response = await axios.post("/api/language/generate-language", {
        userId,
        targetLanguage: pendingLanguage,
        sourceLanguage: "en",
        generateLocationVideos: true,
      });
      if (response.data.success) {
        showNotification(
          "success",
          `Language ${getLanguageName(pendingLanguage)} generated successfully!`,
        );
        fetchLanguages();
        fetchLanguageSettings();
        setTargetLanguage("");
        setSearchQuery("");
        setPendingLanguage(null);
      }
    } catch (error) {
      showNotification(
        "error",
        error.response?.data?.error || "Error generating language translation",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleScheduleLater = async () => {
    if (!scheduledDate || !scheduledTime) {
      showNotification("error", "Please select both date and time");
      return;
    }
    setIsScheduling(true);
    try {
      const langResponse = await axios.post("/api/language/generate-language", {
        userId,
        targetLanguage: pendingLanguage,
        sourceLanguage: "en",
        generateLocationVideos: false,
      });
      if (!langResponse.data.success)
        throw new Error("Failed to generate language");
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      const scheduleResponse = await axios.post(
        "/api/language/schedule-video-generation",
        {
          userId,
          targetLanguage: pendingLanguage,
          sourceLanguage: "en",
          scheduledDateTime: scheduledDateTime.toISOString(),
        },
      );
      if (scheduleResponse.data.success) {
        showNotification(
          "success",
          `Language ${getLanguageName(pendingLanguage)} created! Videos scheduled for ${scheduledDateTime.toLocaleString()}`,
        );
        fetchLanguages();
        fetchLanguageSettings();
        setTargetLanguage("");
        setSearchQuery("");
        setPendingLanguage(null);
        setScheduledDate("");
        setScheduledTime("");
        setShowVideoScheduleModal(false);
      }
    } catch (error) {
      showNotification(
        "error",
        error.response?.data?.error || "Error scheduling video generation",
      );
    } finally {
      setIsScheduling(false);
    }
  };

  const confirmDeleteLanguage = (langCode, langName) => {
    if (langCode === "en") {
      showNotification("error", "Cannot delete English language (default)");
      return;
    }
    setLanguageToDelete({ code: langCode, name: langName });
    setShowConfirmDelete(true);
  };

  const handleDeleteLanguage = async () => {
    if (!languageToDelete) return;
    setIsRemoving(true);
    try {
      const response = await axios.delete(
        `/api/language/remove-language/${userId}/${languageToDelete.code}`,
      );
      if (response.data.success) {
        showNotification(
          "success",
          `Language ${languageToDelete.name} removed successfully!`,
        );
        fetchLanguages();
        fetchLanguageSettings();
      }
    } catch (error) {
      showNotification(
        "error",
        error.response?.data?.error || "Error removing language",
      );
    } finally {
      setIsRemoving(false);
      setShowConfirmDelete(false);
      setLanguageToDelete(null);
    }
  };

  const getLanguageInfo = (code) =>
    availableLanguages.find((lang) => lang.code === code) || {
      code,
      name: getLanguageName(code),
      flag: getLanguageFlag(code),
    };

  const toggleLanguageEnabled = async (langCode, currentEnabled) => {
    if (langCode === "en") {
      showNotification("error", "Cannot disable English language (default)");
      return;
    }
    try {
      const response = await axios.post("/api/language/toggle-enabled", {
        userId,
        languageCode: langCode,
        enabled: !currentEnabled,
      });
      if (response.data.success) {
        showNotification(
          "success",
          `Language ${getLanguageName(langCode)} ${!currentEnabled ? "enabled" : "disabled"} successfully!`,
        );
        fetchLanguageSettings();
      }
    } catch (error) {
      showNotification("error", "Failed to toggle language status");
    }
  };

  const isLanguageEnabled = (langCode) => {
    if (langCode === "en") return true;
    const setting = languageSettings.find((l) => l.code === langCode);
    return setting ? setting.enabled : false;
  };

  // Check if a language has pending or processing jobs
  const hasPendingJobs = (langCode) => {
    return scheduledJobs.some(job => 
      job.targetLanguage === langCode && 
      (job.status === 'pending' || job.status === 'processing')
    );
  };

  // Check if regenerate button should be disabled
  const isRegenerateDisabled = (langCode) => {
    return hasPendingJobs(langCode) || isRegenerating;
  };

  // Handle regenerate language click
  const handleRegenerateLanguage = (langCode, langName) => {
    if (langCode === 'en') {
      showNotification('error', 'Cannot regenerate English language (default)');
      return;
    }
    
    // Check for pending jobs
    if (hasPendingJobs(langCode)) {
      const pendingJobsCount = scheduledJobs.filter(job => 
        job.targetLanguage === langCode && 
        (job.status === 'pending' || job.status === 'processing')
      ).length;
      
      showNotification('error', `Cannot regenerate ${langName}. There are ${pendingJobsCount} pending/processing video job(s). Please wait or cancel them first.`);
      return;
    }
    
    setLanguageToRegenerate({ code: langCode, name: langName });
    setRegenerateVideos(false);
    setScheduleOption('now');
    setScheduledDate('');
    setScheduledTime('');
    setShowRegenerateModal(true);
  };

  // Handle regenerate confirmation
  const handleConfirmRegenerate = async () => {
    if (!languageToRegenerate) return;

    setIsRegenerating(true);
    
    try {
      const requestBody = {
        userId,
        targetLanguage: languageToRegenerate.code,
        sourceLanguage: 'en',
        regenerateVideos: regenerateVideos,
        scheduleVideos: regenerateVideos && scheduleOption === 'later',
        scheduledDateTime: (regenerateVideos && scheduleOption === 'later') 
          ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString() 
          : null
      };

      const response = await axios.post('/api/language/regenerate-language', requestBody);

      if (response.data.success) {
        let message = `Language ${languageToRegenerate.name} regenerated successfully!`;
        
        if (regenerateVideos) {
          if (scheduleOption === 'later') {
            message += ` Videos scheduled for ${new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString()}`;
          } else {
            message += ' Videos are being regenerated.';
          }
        }
        
        showNotification('success', message);
        fetchLanguages();
        fetchLanguageSettings();
        fetchScheduledJobs(); // Refresh scheduled jobs
      }
    } catch (error) {
      console.error('Error regenerating language:', error);
      const errorMessage = error.response?.data?.error || 'Error regenerating language';
      showNotification('error', errorMessage);
    } finally {
      setIsRegenerating(false);
      setShowRegenerateModal(false);
      setLanguageToRegenerate(null);
      setRegenerateVideos(false);
      setScheduleOption('now');
      setScheduledDate('');
      setScheduledTime('');
    }
  };

  const updateCustomization = (key, value) =>
    setLanguageCustomization((prev) => ({ ...prev, [key]: value }));

  // ── Input Helpers ──────────────────────────────────────────────
  const inputStyle = (focused = false) => ({
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: `2px solid ${focused ? buttonColor : "#e2e8f0"}`,
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    backgroundColor: "white",
    color: "#2d3748",
  });

  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    color: "#4a5568",
    fontSize: "13px",
    fontWeight: 600,
  };

  const fieldWrap = { display: "flex", flexDirection: "column" };

  // ── Section Wrapper ────────────────────────────────────────────
  const SectionBox = ({ icon, title, children }) => (
    <div
      style={{
        marginBottom: "20px",
        padding: "20px",
        backgroundColor: "#f8fafc",
        borderRadius: "10px",
        border: "1px solid #e2e8f0",
      }}
    >
      <h4
        style={{
          margin: "0 0 16px 0",
          color: "#2d3748",
          fontSize: "15px",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <i className={icon} style={{ color: buttonColor }}></i> {title}
      </h4>
      {children}
    </div>
  );

  const Grid = ({
    children,
    cols = "repeat(auto-fit, minmax(200px, 1fr))",
  }) => (
    <div style={{ display: "grid", gridTemplateColumns: cols, gap: "15px" }}>
      {children}
    </div>
  );

  const Field = ({ label, children }) => (
    <div style={fieldWrap}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );

  const NumberInput = ({ value, onChange, min, max, placeholder }) => (
    <input
      type="number"
      min={min}
      max={max}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) =>
        onChange(e.target.value === "" ? null : parseInt(e.target.value))
      }
      style={inputStyle()}
      onFocus={(e) => (e.target.style.borderColor = buttonColor)}
      onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
    />
  );

  const ColorInput = ({ value, onChange }) => (
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        height: "42px",
        borderRadius: "8px",
        border: "2px solid #e2e8f0",
        cursor: "pointer",
        boxSizing: "border-box",
      }}
    />
  );

  const SelectInput = ({ value, onChange, options }) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle()}
      onFocus={(e) => (e.target.style.borderColor = buttonColor)}
      onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
    >
      {options.map((opt) => (
        <option key={opt.value ?? opt} value={opt.value ?? opt}>
          {opt.label ?? opt}
        </option>
      ))}
    </select>
  );

  const CheckboxField = ({ checked, onChange, label }) => (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        cursor: "pointer",
        padding: "10px",
        backgroundColor: "white",
        borderRadius: "8px",
        border: "2px solid #e2e8f0",
        userSelect: "none",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          width: "18px",
          height: "18px",
          cursor: "pointer",
          accentColor: buttonColor,
        }}
      />
      <span style={{ color: "#4a5568", fontSize: "14px", fontWeight: 600 }}>
        {label}
      </span>
    </label>
  );

  const SaveButton = ({ onClick, loading, loadingText, text, icon }) => (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        width: "100%",
        padding: "14px",
        backgroundColor: buttonColor,
        color: "white",
        border: "none",
        borderRadius: "10px",
        fontSize: "15px",
        fontWeight: 600,
        cursor: loading ? "not-allowed" : "pointer",
        opacity: loading ? 0.7 : 1,
        transition: "all 0.2s",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
      }}
      onMouseEnter={(e) => {
        if (!loading) {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = `0 6px 15px ${buttonColor}40`;
        }
      }}
      onMouseLeave={(e) => {
        if (!loading) {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }
      }}
    >
      {loading ? (
        <>
          <i className="fas fa-spinner fa-spin"></i> {loadingText}
        </>
      ) : (
        <>
          <i className={icon}></i> {text}
        </>
      )}
    </button>
  );

  const c = languageCustomization;

  return (

    <div
      style={{
        display: "flex",
        flexDirection: 'column',
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100%",
        width: "100%",
      }}
    >
      {/* Notification Toast */}
      {notification.show && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 9999,
            backgroundColor:
              notification.type === "success" ? "#10b981" : "#ef4444",
            color: "white",
            padding: "12px 20px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            animation: "slideIn 0.3s ease-out",
          }}
        >
          <i
            className={`fas ${notification.type === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`}
          ></i>
          <span>{notification.message}</span>
        </div>
      )}

      {/* Processing Warning Banner */}
      {(isGenerating || isRegenerating) && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1001,
          backgroundColor: '#f59e0b',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(245, 158, 11, 0.4)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          animation: 'slideDown 0.3s ease-out',
          minWidth: '400px',
          maxWidth: '90%'
        }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '20px' }}></i>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '15px' }}>
              {isGenerating ? 'Generating Language...' : 'Regenerating Language...'}
            </div>
            <div style={{ fontSize: '13px', opacity: 0.95 }}>
              ⚠️ Please don't refresh or leave this page until the process is complete
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showConfirmDelete && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "30px",
              width: "90%",
              maxWidth: "400px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: "25px" }}>
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  backgroundColor: "#fee2e2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 15px",
                  color: "#dc2626",
                  fontSize: "24px",
                }}
              >
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h3
                style={{
                  margin: "0 0 8px 0",
                  color: "#2d3748",
                  fontSize: "20px",
                  fontWeight: 600,
                }}
              >
                Delete Language
              </h3>
              <p style={{ margin: "0", color: "#718096", fontSize: "14px" }}>
                Are you sure you want to delete{" "}
                <strong>{languageToDelete?.name}</strong>?
              </p>
            </div>
            <div
              style={{
                backgroundColor: "#fef2f2",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "25px",
                border: "1px solid #fecaca",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  color: "#dc2626",
                }}
              >
                <i className="fas fa-exclamation-circle"></i>
                <span style={{ fontSize: "13px", fontWeight: 500 }}>
                  This action cannot be undone
                </span>
              </div>
              <ul
                style={{
                  margin: "10px 0 0 20px",
                  padding: 0,
                  fontSize: "12px",
                  color: "#7c2d12",
                }}
              >
                {/* <li>All translations will be deleted</li> */}
                <li>The language you are deleting media files will be deleted but the original English files will be left alone</li>
                {/* <li>Users using this language will see English</li> */}
              </ul>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => setShowConfirmDelete(false)}
                disabled={isRemoving}
                style={{
                  flex: 1,
                  padding: "12px",
                  backgroundColor: "#f3f4f6",
                  color: "#4b5563",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteLanguage}
                disabled={isRemoving}
                style={{
                  flex: 1,
                  padding: "12px",
                  backgroundColor: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: isRemoving ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  opacity: isRemoving ? 0.7 : 1,
                }}
              >
                {isRemoving ? (
                  <>
                    <i
                      className="fas fa-spinner fa-spin"
                      style={{ marginRight: "8px" }}
                    ></i>
                    Deleting...
                  </>
                ) : (
                  "Delete Language"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Schedule Modal */}
      {showVideoScheduleModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "30px",
              width: "90%",
              maxWidth: "500px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: "25px" }}>
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  backgroundColor: `${buttonColor}20`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 15px",
                  color: buttonColor,
                  fontSize: "24px",
                }}
              >
                <i className="fas fa-video"></i>
              </div>
              <h3
                style={{
                  margin: "0 0 8px 0",
                  color: "#2d3748",
                  fontSize: "20px",
                  fontWeight: 600,
                }}
              >
                Video Generation Schedule
              </h3>
              <p style={{ margin: "0", color: "#718096", fontSize: "14px" }}>
                When would you like to generate videos for{" "}
                <strong>{getLanguageName(pendingLanguage)}</strong>?
              </p>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                marginBottom: "25px",
              }}
            >
              {["now", "later"].map((opt) => (
                <div
                  key={opt}
                  onClick={() => setScheduleOption(opt)}
                  style={{
                    padding: "20px",
                    borderRadius: "10px",
                    border: `2px solid ${scheduleOption === opt ? buttonColor : "#e2e8f0"}`,
                    backgroundColor:
                      scheduleOption === opt ? `${buttonColor}10` : "white",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        border: `2px solid ${scheduleOption === opt ? buttonColor : "#cbd5e0"}`,
                        backgroundColor:
                          scheduleOption === opt ? buttonColor : "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {scheduleOption === opt && (
                        <div
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: "white",
                          }}
                        ></div>
                      )}
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#2d3748",
                          marginBottom: "4px",
                        }}
                      >
                        <i
                          className={`fas ${opt === "now" ? "fa-bolt" : "fa-calendar-alt"}`}
                          style={{ marginRight: "8px", color: buttonColor }}
                        ></i>
                        {opt === "now" ? "Generate Now" : "Schedule for Later"}
                      </div>
                      <div style={{ fontSize: "13px", color: "#718096" }}>
                        {opt === "now"
                          ? "Start video generation immediately"
                          : "Choose a specific date and time"}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {scheduleOption === "later" && (
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "10px",
                  marginBottom: "25px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "15px",
                  }}
                >
                  <div>
                    <label style={labelStyle}>
                      <i
                        className="fas fa-calendar"
                        style={{ marginRight: "6px" }}
                      ></i>
                      Date
                    </label>
                    <input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      style={inputStyle()}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>
                      <i
                        className="fas fa-clock"
                        style={{ marginRight: "6px" }}
                      ></i>
                      Time
                    </label>
                    <input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      style={inputStyle()}
                    />
                  </div>
                </div>
                {scheduledDate && scheduledTime && (
                  <div
                    style={{
                      marginTop: "12px",
                      padding: "10px",
                      backgroundColor: "white",
                      borderRadius: "6px",
                      fontSize: "13px",
                      color: "#4a5568",
                      textAlign: "center",
                    }}
                  >
                    <i
                      className="fas fa-info-circle"
                      style={{ marginRight: "6px", color: buttonColor }}
                    ></i>
                    Videos will be generated on{" "}
                    <strong>
                      {new Date(
                        `${scheduledDate}T${scheduledTime}`,
                      ).toLocaleString()}
                    </strong>
                  </div>
                )}
              </div>
            )}
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => {
                  setShowVideoScheduleModal(false);
                  setPendingLanguage(null);
                  setScheduleOption("now");
                  setScheduledDate("");
                  setScheduledTime("");
                }}
                disabled={isRegenerating}
                style={{
                  flex: 1,
                  padding: "12px",
                  backgroundColor: "#f3f4f6",
                  color: "#4b5563",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={
                  scheduleOption === "now"
                    ? handleGenerateNow
                    : handleScheduleLater
                }
                disabled={
                  isGenerating ||
                  isScheduling ||
                  (scheduleOption === "later" &&
                    (!scheduledDate || !scheduledTime))
                }
                style={{
                  flex: 1,
                  padding: "12px",
                  backgroundColor: buttonColor,
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  cursor: "pointer",
                  opacity:
                    isGenerating ||
                      isScheduling ||
                      (scheduleOption === "later" &&
                        (!scheduledDate || !scheduledTime))
                      ? 0.7
                      : 1,
                }}
              >
                {isRegenerating ? (
                  <>
                    <i
                      className="fas fa-spinner fa-spin"
                      style={{ marginRight: "8px" }}
                    ></i>
                    {scheduleOption === "now"
                      ? "Generating..."
                      : "Scheduling..."}
                  </>
                ) : (
                  <>
                    <i
                      className={`fas ${scheduleOption === "now" ? "fa-bolt" : "fa-calendar-check"}`}
                      style={{ marginRight: "8px" }}
                    ></i>
                    {scheduleOption === "now" ? "Generate Now" : "Schedule"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Language Modal */}
      {showRegenerateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '550px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '25px'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: `${buttonColor}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 15px',
                color: buttonColor,
                fontSize: '24px'
              }}>
                <i className="fas fa-sync-alt"></i>
              </div>
              <h3 style={{
                margin: '0 0 8px 0',
                color: '#2d3748',
                fontSize: '20px',
                fontWeight: 600
              }}>
                Regenerate Language
              </h3>
              <p style={{
                margin: '0',
                color: '#718096',
                fontSize: '14px'
              }}>
                Regenerate translations for <strong>{languageToRegenerate?.name}</strong>
              </p>
            </div>

            {/* Info Box */}
            <div style={{
              backgroundColor: '#f0f9ff',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '25px',
              border: '1px solid #bae6fd'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                color: '#0369a1'
              }}>
                <i className="fas fa-info-circle" style={{ marginTop: '2px' }}></i>
                <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                  <strong>What will be regenerated:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>All text translations will be UPDATED from English</li>
                    <li>Missing translation keys will be ADDED</li>
                    <li>Existing media files will be PRESERVED</li>
                    <li>Language settings (enabled/disabled) will remain unchanged</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Video Regeneration Option */}
            <div style={{
              marginBottom: '25px'
            }}>
              <div
                onClick={() => setRegenerateVideos(!regenerateVideos)}
                style={{
                  padding: '20px',
                  borderRadius: '10px',
                  border: `2px solid ${regenerateVideos ? buttonColor : '#e2e8f0'}`,
                  backgroundColor: regenerateVideos ? `${buttonColor}10` : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '6px',
                    border: `2px solid ${regenerateVideos ? buttonColor : '#cbd5e0'}`,
                    backgroundColor: regenerateVideos ? buttonColor : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    {regenerateVideos && '✓'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 600,
                      color: '#2d3748',
                      marginBottom: '4px',
                      fontSize: '15px'
                    }}>
                      <i className="fas fa-video" style={{ marginRight: '8px', color: buttonColor }}></i>
                      Regenerate HeyGen Videos
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#718096'
                    }}>
                      Re-generate all location videos with updated translations
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Video Schedule Options (shown when regenerateVideos is true) */}
            {regenerateVideos && (
              <div style={{
                marginBottom: '25px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '10px',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{
                  margin: '0 0 15px 0',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#2d3748'
                }}>
                  <i className="fas fa-clock" style={{ marginRight: '8px', color: buttonColor }}></i>
                  Video Generation Schedule
                </h4>

                {/* Schedule Options */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  marginBottom: '15px'
                }}>
                  {/* Generate Now Option */}
                  <div
                    onClick={() => setScheduleOption('now')}
                    style={{
                      padding: '15px',
                      borderRadius: '8px',
                      border: `2px solid ${scheduleOption === 'now' ? buttonColor : '#e2e8f0'}`,
                      backgroundColor: scheduleOption === 'now' ? `${buttonColor}10` : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: `2px solid ${scheduleOption === 'now' ? buttonColor : '#cbd5e0'}`,
                        backgroundColor: scheduleOption === 'now' ? buttonColor : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {scheduleOption === 'now' && (
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: 'white'
                          }}></div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontWeight: 600,
                          color: '#2d3748',
                          fontSize: '14px'
                        }}>
                          <i className="fas fa-bolt" style={{ marginRight: '6px', color: buttonColor }}></i>
                          Generate Now
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Schedule for Later Option */}
                  <div
                    onClick={() => setScheduleOption('later')}
                    style={{
                      padding: '15px',
                      borderRadius: '8px',
                      border: `2px solid ${scheduleOption === 'later' ? buttonColor : '#e2e8f0'}`,
                      backgroundColor: scheduleOption === 'later' ? `${buttonColor}10` : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: `2px solid ${scheduleOption === 'later' ? buttonColor : '#cbd5e0'}`,
                        backgroundColor: scheduleOption === 'later' ? buttonColor : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {scheduleOption === 'later' && (
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: 'white'
                          }}></div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontWeight: 600,
                          color: '#2d3748',
                          fontSize: '14px'
                        }}>
                          <i className="fas fa-calendar-alt" style={{ marginRight: '6px', color: buttonColor }}></i>
                          Schedule for Later
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Date/Time Picker (shown when "later" is selected) */}
                {scheduleOption === 'later' && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px'
                    }}>
                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '6px',
                          color: '#4a5568',
                          fontSize: '13px',
                          fontWeight: 600
                        }}>
                          Date
                        </label>
                        <input
                          type="date"
                          value={scheduledDate}
                          onChange={(e) => setScheduledDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '6px',
                            border: '2px solid #e2e8f0',
                            fontSize: '13px',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '6px',
                          color: '#4a5568',
                          fontSize: '13px',
                          fontWeight: 600
                        }}>
                          Time
                        </label>
                        <input
                          type="time"
                          value={scheduledTime}
                          onChange={(e) => setScheduledTime(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '6px',
                            border: '2px solid #e2e8f0',
                            fontSize: '13px',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>
                    {scheduledDate && scheduledTime && (
                      <div style={{
                        marginTop: '10px',
                        padding: '8px',
                        backgroundColor: '#f0f9ff',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#0369a1',
                        textAlign: 'center'
                      }}>
                        <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                        Scheduled for <strong>{new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString()}</strong>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowRegenerateModal(false);
                  setLanguageToRegenerate(null);
                  setRegenerateVideos(false);
                  setScheduleOption('now');
                  setScheduledDate('');
                  setScheduledTime('');
                }}
                disabled={isRegenerating}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#f3f4f6',
                  color: '#4b5563',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRegenerate}
                disabled={isRegenerating || (regenerateVideos && scheduleOption === 'later' && (!scheduledDate || !scheduledTime))}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: buttonColor,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (isRegenerating || (regenerateVideos && scheduleOption === 'later' && (!scheduledDate || !scheduledTime))) ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  transition: 'all 0.2s',
                  opacity: (isRegenerating || (regenerateVideos && scheduleOption === 'later' && (!scheduledDate || !scheduledTime))) ? 0.7 : 1
                }}
              >
                {isRegenerating ? (
                  <>
                    <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                    Regenerating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i>
                    Regenerate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="language-selector"
        style={{ width: "100%", margin: "0 auto", textAlign: "center" }}
      >
        {/* Header */}
        <div style={{ marginBottom: "30px", textAlign: "center" }}>
          <h2
            style={{
              margin: "0 0 8px 0",
              color: "#2d3748",
              fontSize: "24px",
              fontWeight: 600,
            }}
          >
            Language Translator
          </h2>
          <p style={{ margin: "0", color: "#718096", fontSize: "14px" }}>
            Manage and generate languages for your application
          </p>
        </div>

        {/* Generated Languages */}
        <div
          style={{
            marginBottom: "30px",
            backgroundColor: "#f8f9fa",
            padding: "25px",
            borderRadius: "12px",
            border: "1px solid #e9ecef",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              marginBottom: "20px",
            }}
          >
            <i
              className="fas fa-check-circle"
              style={{ color: buttonColor, fontSize: "20px" }}
            ></i>
            <h3
              style={{
                margin: "0",
                color: "#2d3748",
                fontSize: "18px",
                fontWeight: 600,
              }}
            >
              Generated Languages
            </h3>
          </div>
          {languages.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "20px", color: "#718096" }}
            >
              <i
                className="fas fa-language"
                style={{ fontSize: "48px", marginBottom: "12px", opacity: 0.3 }}
              ></i>
              <p style={{ margin: "0 0 8px 0", fontWeight: 500 }}>
                No languages generated yet
              </p>
              <p style={{ margin: 0, fontSize: "13px" }}>
                Generate your first language below
              </p>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                  gap: "16px",
                  marginBottom: "20px",
                }}
              >
                {languages.map((langCode) => {
                  const langInfo = getLanguageInfo(langCode);
                  const isDefault = langCode === "en";
                  const enabled = isLanguageEnabled(langCode);
                  return (
                    <div
                      key={langCode}
                      style={{
                        padding: "16px",
                        borderRadius: "10px",
                        border: `2px solid ${enabled ? buttonColor : "#cbd5e0"}`,
                        backgroundColor: enabled ? "white" : "#f7fafc",
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "10px",
                        transition: "all 0.2s",
                        position: "relative",
                        opacity: enabled ? 1 : 0.6,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(0,0,0,0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      {/* Status Badge */}
                      <div style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        backgroundColor: enabled ? (isDefault ? '#3b82f6' : buttonColor) : '#cbd5e0',
                        color: 'white',
                        width: '26px',
                        height: '26px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        fontWeight: 'bold'
                      }}>
                        {isDefault ? '★' : (enabled ? '✓' : '○')}
                      </div>
                      <span
                        className={`fi fi-${langInfo.flag}`}
                        style={{ 
                          fontSize: "40px",
                          borderRadius: "6px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          marginTop: '4px'
                        }}
                      ></span>
                      <div style={{ width: '100%' }}>
                        <div style={{ 
                          fontWeight: 600, 
                          fontSize: '15px', 
                          color: enabled ? (isDefault ? '#3b82f6' : '#2d3748') : '#718096',
                          marginBottom: '4px'
                        }}>
                          {langInfo.name}
                        </div>
                        {isDefault && (
                          <div style={{
                            fontSize: '10px',
                            color: '#60a5fa',
                            marginBottom: '4px'
                          }}>
                            (Default)
                          </div>
                        )}
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#718096',
                          backgroundColor: isDefault ? '#dbeafe' : (enabled ? '#f1f5f9' : '#e2e8f0'),
                          padding: '3px 8px',
                          borderRadius: '10px',
                          display: 'inline-block',
                          marginBottom: '10px',
                          fontWeight: '500'
                        }}>
                          {langCode.toUpperCase()}
                        </div>
                        {!isDefault && (
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                            paddingTop: '10px',
                            borderTop: '1px solid #e2e8f0'
                          }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleLanguageEnabled(langCode, enabled);
                              }}
                              style={{
                                width: '100%',
                                padding: "7px 10px",
                                backgroundColor: enabled ? "#10b981" : "#6b7280",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                fontSize: "11px",
                                fontWeight: "600",
                                cursor: "pointer",
                                transition: 'all 0.2s',
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "5px",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.opacity = '0.85';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.opacity = '1';
                              }}
                            >
                              <i className={`fas ${enabled ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                              {enabled ? 'Enabled' : 'Disabled'}
                            </button>
                            
                            <div style={{
                              display: 'flex',
                              gap: '6px'
                            }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRegenerateLanguage(langCode, langInfo.name);
                                }}
                                disabled={isRegenerateDisabled(langCode)}
                                style={{
                                  flex: 1,
                                  padding: '7px 8px',
                                  backgroundColor: isRegenerateDisabled(langCode) ? '#94a3b8' : '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  cursor: isRegenerateDisabled(langCode) ? 'not-allowed' : 'pointer',
                                  transition: 'all 0.2s',
                                  opacity: isRegenerateDisabled(langCode) ? 0.6 : 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '4px'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isRegenerateDisabled(langCode)) {
                                    e.target.style.backgroundColor = '#2563eb';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isRegenerateDisabled(langCode)) {
                                    e.target.style.backgroundColor = '#3b82f6';
                                  }
                                }}
                                title={isRegenerateDisabled(langCode) ? "Cannot regenerate - pending jobs exist" : "Regenerate language"}
                              >
                                <i className="fas fa-sync-alt"></i>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmDeleteLanguage(langCode, langInfo.name);
                                }}
                                style={{
                                  flex: 1,
                                  padding: '7px 8px',
                                  backgroundColor: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '4px'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#dc2626';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = '#ef4444';
                                }}
                                title="Delete language"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "15px",
                  padding: "12px",
                  backgroundColor: "white",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <i className="fas fa-flag" style={{ color: buttonColor }}></i>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#4a5568",
                      fontWeight: 500,
                    }}
                  >
                    {languages.length} language
                    {languages.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div
                  style={{
                    width: "1px",
                    height: "20px",
                    backgroundColor: "#e2e8f0",
                  }}
                ></div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <i
                    className="fas fa-check-circle"
                    style={{ color: "#10b981" }}
                  ></i>
                  <span style={{ fontSize: "12px", color: "#718096" }}>
                    {languageSettings.filter((l) => l.enabled).length} enabled
                  </span>
                </div>
                <div
                  style={{
                    width: "1px",
                    height: "20px",
                    backgroundColor: "#e2e8f0",
                  }}
                ></div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <i
                    className="fas fa-info-circle"
                    style={{ color: "#3b82f6" }}
                  ></i>
                  <span style={{ fontSize: "12px", color: "#718096" }}>
                    Toggle to enable/disable
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Language Position Settings */}
        <div
          style={{
            marginBottom: "30px",
            backgroundColor: "white",
            padding: "25px",
            borderRadius: "12px",
            border: "2px solid #e2e8f0",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              marginBottom: "20px",
            }}
          >
            <i
              className="fas fa-sliders-h"
              style={{ color: buttonColor, fontSize: "20px" }}
            ></i>
            <h3
              style={{
                margin: "0",
                color: "#2d3748",
                fontSize: "18px",
                fontWeight: 600,
              }}
            >
              Language Flags Position
            </h3>
          </div>
          <p
            style={{
              margin: "0 0 20px 0",
              color: "#718096",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Control where language flags appear on the screen
          </p>
          <div
            style={{
              marginBottom: "25px",
              padding: "50px",
              backgroundColor: "#f8f9fa",
              borderRadius: "10px",
              border: "1px dashed #cbd5e0",
              position: "relative",
              // height: "300px",
            }}
          >

            <div style={{ width: 'fit-content' }}>
              <div
                style={{
                  // position: "absolute",
                  // left: `${languageOffsets.left}%`,
                  // top: `${languageOffsets.top}%`,
                  // transform: "translate(-50%, -50%)",
                  backgroundColor: `rgba(${parseInt(c.outerBackgroundColor.slice(1, 3), 16)}, ${parseInt(c.outerBackgroundColor.slice(3, 5), 16)}, ${parseInt(c.outerBackgroundColor.slice(5, 7), 16)}, ${c.outerOpacity / 100})`,
                  padding: `${pxToVw(c.outerPadding || 20)}`,
                  borderRadius: `${c.outerBorderRadius}px`,
                  border: `${c.outerBorderWidth}px solid ${c.outerBorderColor}`,
                  boxShadow: (() => {
                    const h = c.outerShadowH || 0;
                    const v = c.outerShadowV || 4;
                    const blur = c.outerShadowBlur || 20;
                    const spread = c.outerShadowSpread || 0;
                    const color = c.outerShadowColor || '#000000';
                    const opacity = c.outerShadowOpacity || 40;
                    const r = parseInt(color.slice(1, 3), 16);
                    const g = parseInt(color.slice(3, 5), 16);
                    const b = parseInt(color.slice(5, 7), 16);
                    return `${h}px ${v}px ${blur}px ${spread}px rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
                  })(),
                  display: "flex",
                  flexDirection: "column",
                  gap: `${c.gap}px`,
                  width: c.outerWidth ? pxToVw(c.outerWidth) : 'auto',
                  // height: c.outerHeight ? pxToVw(c.outerHeight) : 'auto',
                }}
              >
                {languages.slice(0, 2).map((langCode, index) => {
                  const langInfo = getLanguageInfo(langCode);
                  return (
                    <LanguageBox
                      key={langCode}
                      language={langInfo}
                      customization={c}
                      isSelected={index === 0}
                      size="small"
                      customIconUrl={languageIcons[langCode]}
                      customIconSize={languageIconSizes[langCode]}
                      customDisplayName={languageDisplayNames[langCode]}
                    />
                  );
                })}
              </div></div>

            <div
              style={{
                position: "absolute",
                bottom: "10px",
                left: "50%",
                transform: "translateX(-50%)",
                fontSize: "12px",
                color: "#718096",
                backgroundColor: "white",
                padding: "4px 12px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              Preview Position
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "20px",
              marginBottom: "20px",
            }}
          >
            {[
              {
                key: "left",
                label: "Left Offset (%)",
                icon: "fa-arrow-left",
                desc: "Distance from left edge (0-100%)",
              },
              {
                key: "top",
                label: "Top Offset (%)",
                icon: "fa-arrow-up",
                desc: "Distance from top edge (0-100%)",
              },
              {
                key: "right",
                label: "Right Offset (%)",
                icon: "fa-arrow-right",
                desc: "Distance from right edge (optional)",
                optional: true,
              },
            ].map(({ key, label, icon, desc, optional }) => (
              <div key={key}>
                <label style={labelStyle}>
                  <i
                    className={`fas ${icon}`}
                    style={{ marginRight: "8px", color: buttonColor }}
                  ></i>
                  {label}
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={languageOffsets[key] ?? ""}
                  placeholder={optional ? "Optional" : ""}
                  onChange={(e) =>
                    setLanguageOffsets({
                      ...languageOffsets,
                      [key]: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  style={inputStyle()}
                  onFocus={(e) => (e.target.style.borderColor = buttonColor)}
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
                <p
                  style={{
                    margin: "6px 0 0 0",
                    fontSize: "12px",
                    color: "#718096",
                  }}
                >
                  {desc}
                </p>
              </div>
            ))}
          </div>
          <SaveButton
            onClick={saveLanguageOffsets}
            loading={isSavingOffsets}
            loadingText="Saving Position..."
            text="Save Language Position"
            icon="fas fa-save"
          />
        </div>

        {/* ═══════════════════════════════════════════════════════════
            LANGUAGE SELECTOR CUSTOMIZATION
        ═══════════════════════════════════════════════════════════ */}
        <div
          style={{
            marginBottom: "30px",
            backgroundColor: "white",
            padding: "25px",
            borderRadius: "12px",
            border: "2px solid #e2e8f0",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              marginBottom: "8px",
            }}
          >
            <i
              className="fas fa-paint-brush"
              style={{ color: buttonColor, fontSize: "20px" }}
            ></i>
            <h3
              style={{
                margin: "0",
                color: "#2d3748",
                fontSize: "18px",
                fontWeight: 600,
              }}
            >
              Language Selector Customization
            </h3>
          </div>
          <p
            style={{
              margin: "0 0 25px 0",
              color: "#718096",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Customize the appearance of language boxes on the frontend
          </p>

          {/* Live Preview */}
          {/* <div
            style={{
              marginBottom: "25px",
              padding: "30px",
              backgroundColor: `rgba(${parseInt(c.outerBackgroundColor.slice(1, 3), 16)}, ${parseInt(c.outerBackgroundColor.slice(3, 5), 16)}, ${parseInt(c.outerBackgroundColor.slice(5, 7), 16)}, ${c.outerOpacity / 100})`,
              borderRadius: `${c.outerBorderRadius}px`,
              border: `${c.outerBorderWidth}px solid ${c.outerBorderColor}`,
              display: "flex",
              flexDirection: "column",
              gap: `${c.gap}px`,
              alignItems: "center",
            }}
          >
            {languages.slice(0, 3).map((langCode, index) => {
              const langInfo = getLanguageInfo(langCode);
              return (
                <LanguageBox
                  key={langCode}
                  language={langInfo}
                  customization={c}
                  isSelected={index === 0}
                  size="normal"
                  customIconUrl={languageIcons[langCode]}
                />
              );
            })}
          </div> */}

          {/* Customization Form */}
          <div className="row">
            <div className="col-md-12">
              <h5 style={{
                margin: "0 0 16px 0",
                color: "#2d3748",
                fontSize: "15px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
                <i className="fas fa-square" style={{ color: buttonColor }}></i> Outer Box Settings
              </h5>
            </div>

            {/* Outer Box Background Color */}
            <div className="form-group col-md-4">
              <label>Outer Background Color:</label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={c.outerBackgroundColor}
                onChange={(newHex) =>
                  updateCustomization("outerBackgroundColor", newHex?.toUpperCase?.())
                }
                isAlphaHidden={true}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px !important",
                    height: 53,
                  },
                  "& .MuiOutlinedInput-input": { height: "36px" },
                  "& .MuiColorInput-Button": {
                    width: "49px !important",
                    height: "40px !important",
                  },
                  "& .MuiInputAdornment-root .MuiBox-root": {
                    width: 18,
                    height: 18,
                  },
                }}
              />
            </div>

            {/* Outer Opacity */}
            <div className="form-group col-md-4">
              <label>Outer Opacity (%):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="100"
                value={c.outerOpacity}
                onChange={(e) =>
                  updateCustomization("outerOpacity", parseInt(e.target.value) || 0)
                }
              />
            </div>

            {/* Outer Border Color */}
            <div className="form-group col-md-4">
              <label>Outer Border Color:</label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={c.outerBorderColor}
                onChange={(newHex) =>
                  updateCustomization("outerBorderColor", newHex?.toUpperCase?.())
                }
                isAlphaHidden={true}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px !important",
                    height: 53,
                  },
                  "& .MuiOutlinedInput-input": { height: "36px" },
                  "& .MuiColorInput-Button": {
                    width: "49px !important",
                    height: "40px !important",
                  },
                  "& .MuiInputAdornment-root .MuiBox-root": {
                    width: 18,
                    height: 18,
                  },
                }}
              />
            </div>

            {/* Outer Border Radius */}
            <div className="form-group col-md-4">
              <label>Outer Border Radius (px):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="50"
                value={c.outerBorderRadius}
                onChange={(e) =>
                  updateCustomization("outerBorderRadius", parseInt(e.target.value) || 0)
                }
              />
            </div>

            {/* Outer Border Width */}
            <div className="form-group col-md-4">
              <label>Outer Border Width (px):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="10"
                value={c.outerBorderWidth}
                onChange={(e) =>
                  updateCustomization("outerBorderWidth", parseInt(e.target.value) || 0)
                }
              />
            </div>

            {/* Outer Padding */}
            <div className="form-group col-md-4">
              <label>Outer Padding (px):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="50"
                value={c.outerPadding}
                onChange={(e) =>
                  updateCustomization("outerPadding", parseInt(e.target.value) || 0)
                }
              />
            </div>

            {/* Gap */}
            <div className="form-group col-md-4">
              <label>Gap Between Items (px):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="50"
                value={c.gap}
                onChange={(e) =>
                  updateCustomization("gap", parseInt(e.target.value) || 0)
                }
              />
            </div>

            {/* Outer Width */}
            <div className="form-group col-md-4">
              <label>Outer Width (px):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="800"
                value={c.outerWidth ?? ""}
                placeholder="Auto"
                onChange={(e) =>
                  updateCustomization("outerWidth", e.target.value === "" ? null : parseInt(e.target.value))
                }
              />
              <small style={{ fontSize: "11px", color: "#718096" }}>
                Leave empty for auto width
              </small>
            </div>

            {/* Outer Height */}
            <div className="form-group col-md-4">
              <label>Outer Height (px):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="600"
                value={c.outerHeight ?? ""}
                placeholder="Auto"
                onChange={(e) =>
                  updateCustomization("outerHeight", e.target.value === "" ? null : parseInt(e.target.value))
                }
              />
              <small style={{ fontSize: "11px", color: "#718096" }}>
                Leave empty for auto height
              </small>
            </div>

            <div className="col-md-12" style={{ marginTop: "20px" }}>
              <h5 style={{
                margin: "0 0 16px 0",
                color: "#2d3748",
                fontSize: "15px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
                <i className="fas fa-moon" style={{ color: buttonColor }}></i> Outer Box Shadow
              </h5>
            </div>

            {/* Outer Shadow Horizontal */}
            <div className="form-group col-md-4">
              <label>Shadow Horizontal (px):</label>
              <input
                type="number"
                className="form-control"
                min="-50"
                max="50"
                value={c.outerShadowH}
                onChange={(e) =>
                  updateCustomization("outerShadowH", parseInt(e.target.value) || 0)
                }
              />
            </div>

            {/* Outer Shadow Vertical */}
            <div className="form-group col-md-4">
              <label>Shadow Vertical (px):</label>
              <input
                type="number"
                className="form-control"
                min="-50"
                max="50"
                value={c.outerShadowV}
                onChange={(e) =>
                  updateCustomization("outerShadowV", parseInt(e.target.value) || 0)
                }
              />
            </div>

            {/* Outer Shadow Blur */}
            <div className="form-group col-md-4">
              <label>Shadow Blur (px):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="100"
                value={c.outerShadowBlur}
                onChange={(e) =>
                  updateCustomization("outerShadowBlur", parseInt(e.target.value) || 0)
                }
              />
            </div>

            {/* Outer Shadow Spread */}
            <div className="form-group col-md-4">
              <label>Shadow Spread (px):</label>
              <input
                type="number"
                className="form-control"
                min="-50"
                max="50"
                value={c.outerShadowSpread}
                onChange={(e) =>
                  updateCustomization("outerShadowSpread", parseInt(e.target.value) || 0)
                }
              />
            </div>

            {/* Outer Shadow Color */}
            <div className="form-group col-md-4">
              <label>Shadow Color:</label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={c.outerShadowColor}
                onChange={(newHex) =>
                  updateCustomization("outerShadowColor", newHex?.toUpperCase?.())
                }
                isAlphaHidden={true}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px !important",
                    height: 53,
                  },
                  "& .MuiOutlinedInput-input": { height: "36px" },
                  "& .MuiColorInput-Button": {
                    width: "49px !important",
                    height: "40px !important",
                  },
                  "& .MuiInputAdornment-root .MuiBox-root": {
                    width: 18,
                    height: 18,
                  },
                }}
              />
            </div>

            {/* Outer Shadow Opacity */}
            <div className="form-group col-md-4">
              <label>Shadow Opacity (%):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="100"
                value={c.outerShadowOpacity}
                onChange={(e) =>
                  updateCustomization("outerShadowOpacity", parseInt(e.target.value) || 0)
                }
              />
            </div>

            <div className="col-md-12" style={{ marginTop: "20px" }}>
              <h5 style={{
                margin: "0 0 16px 0",
                color: "#2d3748",
                fontSize: "15px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
                <i className="fas fa-square" style={{ color: buttonColor }}></i> Inner Box Settings
              </h5>
            </div>

            {/* Inner Box Background Color */}
            <div className="form-group col-md-4">
              <label>Inner Background Color:</label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={c.innerBackgroundColor}
                onChange={(newHex) =>
                  updateCustomization("innerBackgroundColor", newHex?.toUpperCase?.())
                }
                isAlphaHidden={true}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px !important",
                    height: 53,
                  },
                  "& .MuiOutlinedInput-input": { height: "36px" },
                  "& .MuiColorInput-Button": {
                    width: "49px !important",
                    height: "40px !important",
                  },
                  "& .MuiInputAdornment-root .MuiBox-root": {
                    width: 18,
                    height: 18,
                  },
                }}
              />
            </div>

            {/* Inner Opacity */}
            <div className="form-group col-md-4">
              <label>Inner Opacity (%):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="100"
                value={c.innerOpacity}
                onChange={(e) =>
                  updateCustomization("innerOpacity", parseInt(e.target.value) || 0)
                }
              />
            </div>

            {/* Inner Border Color */}
            <div className="form-group col-md-4">
              <label>Inner Border Color:</label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={c.innerBorderColor}
                onChange={(newHex) =>
                  updateCustomization("innerBorderColor", newHex?.toUpperCase?.())
                }
                isAlphaHidden={true}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px !important",
                    height: 53,
                  },
                  "& .MuiOutlinedInput-input": { height: "36px" },
                  "& .MuiColorInput-Button": {
                    width: "49px !important",
                    height: "40px !important",
                  },
                  "& .MuiInputAdornment-root .MuiBox-root": {
                    width: 18,
                    height: 18,
                  },
                }}
              />
            </div>

            {/* Inner Border Radius */}
            <div className="form-group col-md-4">
              <label>Inner Border Radius (px):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="50"
                value={c.innerBorderRadius}
                onChange={(e) =>
                  updateCustomization("innerBorderRadius", parseInt(e.target.value) || 0)
                }
              />
            </div>

            {/* Inner Border Width */}
            <div className="form-group col-md-4">
              <label>Inner Border Width (px):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="10"
                value={c.innerBorderWidth}
                onChange={(e) =>
                  updateCustomization("innerBorderWidth", parseInt(e.target.value) || 0)
                }
              />
            </div>

            {/* Inner Padding */}
            <div className="form-group col-md-4">
              <label>Inner Padding (px):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="50"
                value={c.innerPadding}
                onChange={(e) =>
                  updateCustomization("innerPadding", parseInt(e.target.value) || 0)
                }
              />
            </div>

            {/* <div className="col-md-12" style={{ marginTop: "20px" }}>
              <h5 style={{
                margin: "0 0 16px 0",
                color: "#2d3748",
                fontSize: "15px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
                <i className="fas fa-moon" style={{ color: buttonColor }}></i> Inner Box Shadow
              </h5>
            </div> */}

            {/* Inner Shadow Horizontal */}
            {/* <div className="form-group col-md-4">
              <label>Shadow Horizontal (px):</label>
              <input
                type="number"
                className="form-control"
                min="-50"
                max="50"
                value={c.innerShadowH}
                onChange={(e) =>
                  updateCustomization("innerShadowH", parseInt(e.target.value) || 0)
                }
              />
            </div>

           
            <div className="form-group col-md-4">
              <label>Shadow Vertical (px):</label>
              <input
                type="number"
                className="form-control"
                min="-50"
                max="50"
                value={c.innerShadowV}
                onChange={(e) =>
                  updateCustomization("innerShadowV", parseInt(e.target.value) || 0)
                }
              />
            </div>

            
            <div className="form-group col-md-4">
              <label>Shadow Blur (px):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="100"
                value={c.innerShadowBlur}
                onChange={(e) =>
                  updateCustomization("innerShadowBlur", parseInt(e.target.value) || 0)
                }
              />
            </div>

            
            <div className="form-group col-md-4">
              <label>Shadow Spread (px):</label>
              <input
                type="number"
                className="form-control"
                min="-50"
                max="50"
                value={c.innerShadowSpread}
                onChange={(e) =>
                  updateCustomization("innerShadowSpread", parseInt(e.target.value) || 0)
                }
              />
            </div>

            
            <div className="form-group col-md-4">
              <label>Shadow Color:</label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={c.innerShadowColor}
                onChange={(newHex) =>
                  updateCustomization("innerShadowColor", newHex?.toUpperCase?.())
                }
                isAlphaHidden={true}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px !important",
                    height: 53,
                  },
                  "& .MuiOutlinedInput-input": { height: "36px" },
                  "& .MuiColorInput-Button": {
                    width: "49px !important",
                    height: "40px !important",
                  },
                  "& .MuiInputAdornment-root .MuiBox-root": {
                    width: 18,
                    height: 18,
                  },
                }}
              />
            </div> */}

             
            {/* <div className="form-group col-md-4">
              <label>Shadow Opacity (%):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="100"
                value={c.innerShadowOpacity}
                onChange={(e) =>
                  updateCustomization("innerShadowOpacity", parseInt(e.target.value) || 0)
                }
              />
            </div> */}

            <div className="col-md-12" style={{ marginTop: "20px" }}>
              <h5 style={{
                margin: "0 0 16px 0",
                color: "#2d3748",
                fontSize: "15px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
                <i className="fas fa-font" style={{ color: buttonColor }}></i> Text Settings
              </h5>
            </div>

            {/* Text Color */}
            <div className="form-group col-md-4">
              <label>Text Color:</label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={c.textColor}
                onChange={(newHex) =>
                  updateCustomization("textColor", newHex?.toUpperCase?.())
                }
                isAlphaHidden={true}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px !important",
                    height: 53,
                  },
                  "& .MuiOutlinedInput-input": { height: "36px" },
                  "& .MuiColorInput-Button": {
                    width: "49px !important",
                    height: "40px !important",
                  },
                  "& .MuiInputAdornment-root .MuiBox-root": {
                    width: 18,
                    height: 18,
                  },
                }}
              />
            </div>

            {/* Font Family */}
            <div className="form-group col-md-4">
              <label>Font Family:</label>
              <select
                className="form-control"
                value={c.fontFamily}
                onChange={(e) => updateCustomization("fontFamily", e.target.value)}
              >
                {FONT_FAMILIES.map((font) => (
                  <option key={font.value} value={font.value}>
                    {font.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Font Size */}
            <div className="form-group col-md-4">
              <label>Font Size (px):</label>
              <input
                type="number"
                className="form-control"
                min="8"
                max="48"
                value={c.fontSize}
                onChange={(e) =>
                  updateCustomization("fontSize", parseInt(e.target.value) || 14)
                }
              />
            </div>

            {/* Font Weight */}
            <div className="form-group col-md-4">
              <label>Font Weight:</label>
              <select
                className="form-control"
                value={c.fontWeight}
                onChange={(e) => updateCustomization("fontWeight", e.target.value)}
              >
                <option value="normal">Normal</option>
                <option value="bold">Bold</option>
                <option value="100">100 – Thin</option>
                <option value="300">300 – Light</option>
                <option value="400">400 – Regular</option>
                <option value="500">500 – Medium</option>
                <option value="600">600 – Semi Bold</option>
                <option value="700">700 – Bold</option>
                <option value="800">800 – Extra Bold</option>
                <option value="900">900 – Black</option>
              </select>
            </div>

            {/* Flag/Icon Size */}
            <div className="form-group col-md-4">
              <label>Flag/Icon Size (px):</label>
              <input
                type="number"
                className="form-control"
                min="0"
                // max="100"
                value={c.flagSize}
                onChange={(e) =>
                  updateCustomization("flagSize", parseInt(e.target.value) || 60)
                }
              />
            </div>
          </div>

          {/* Icon Settings Section */}
          <div style={{
            marginTop: "20px",
            padding: "20px",
            backgroundColor: "#f8fafc",
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
          }}>
            <h4 style={{
              margin: "0 0 16px 0",
              color: "#2d3748",
              fontSize: "15px",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}>
              <i className="fas fa-icons" style={{ color: buttonColor }}></i> Icon Settings
            </h4>

            <div className="row">
              {/* Enable Icon Toggle */}
              <div className="form-group col-md-12">
                <label style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  cursor: "pointer",
                  padding: "12px",
                  backgroundColor: "white",
                  borderRadius: "8px",
                  border: "2px solid #e2e8f0",
                  userSelect: "none",
                }}>
                  <input
                    type="checkbox"
                    checked={c.enableIcon}
                    onChange={(e) => updateCustomization("enableIcon", e.target.checked)}
                    style={{
                      width: "20px",
                      height: "20px",
                      cursor: "pointer",
                      accentColor: buttonColor,
                    }}
                  />
                  <span style={{ color: "#4a5568", fontSize: "14px", fontWeight: 600 }}>
                    Enable Icon/Flag Display
                  </span>
                  <span style={{ marginLeft: "auto", fontSize: "12px", color: "#718096" }}>
                    {c.enableIcon ? "Showing flags/icons" : "Text only"}
                  </span>
                </label>
              </div>

              {/* Per-Language Custom Icons */}
              {c.enableIcon && languages.length > 0 && (
                <div className="form-group col-md-12">
                  <h5 style={{
                    margin: "16px 0 12px 0",
                    color: "#2d3748",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}>
                    Custom Icons Per Language
                  </h5>
                  <p style={{
                    margin: "0 0 16px 0",
                    fontSize: "12px",
                    color: "#718096",
                  }}>
                    Upload custom icons for each language. If no custom icon is uploaded, the country flag will be displayed.
                  </p>

                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: "15px",
                  }}>
                    {languages.map((langCode) => {
                      const langInfo = getLanguageInfo(langCode);
                      const hasCustomIcon = languageIcons[langCode];
                      const hasPreview = languageIconPreviews[langCode];
                      const iconSize = languageIconSizes[langCode] || 60;

                      return (
                        <div
                          key={langCode}
                          style={{
                            padding: "15px",
                            backgroundColor: "white",
                            borderRadius: "8px",
                            border: "2px solid #e2e8f0",
                          }}
                        >
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            marginBottom: "12px",
                          }}>
                            {hasPreview ? (
                              <img
                                src={languageIconPreviews[langCode]}
                                alt={`${langInfo.name} preview`}
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  objectFit: "contain",
                                  borderRadius: "4px",
                                  border: "2px solid #fbbf24",
                                  boxShadow: "0 0 8px rgba(251, 191, 36, 0.3)",
                                }}
                              />
                            ) : hasCustomIcon ? (
                              <img
                                src={languageIcons[langCode]}
                                alt={langInfo.name}
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  objectFit: "contain",
                                  borderRadius: "4px",
                                  border: "1px solid #e2e8f0",
                                }}
                              />
                            ) : (
                              <span
                                className={`fi fi-${langInfo.flag}`}
                                style={{
                                  fontSize: "32px",
                                  borderRadius: "4px",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                }}
                              />
                            )}
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#2d3748",
                              }}>
                                {langInfo.name}
                              </div>
                              <div style={{
                                fontSize: "11px",
                                color: hasPreview ? "#f59e0b" : "#718096",
                              }}>
                                {hasPreview ? "Preview" : hasCustomIcon ? "Custom Icon" : "Default Flag"}
                              </div>
                            </div>
                          </div>

                          {/* Icon Size Input - Show if custom icon exists or preview is shown */}
                          {(hasCustomIcon || hasPreview) && (
                            <div style={{ marginBottom: "12px" }}>
                              <label style={{
                                display: "block",
                                fontSize: "11px",
                                color: "#718096",
                                marginBottom: "4px",
                                fontWeight: 600,
                              }}>
                                Icon Size (px)
                              </label>
                              <input
                                type="number"
                                min="0"
                                // max="100"
                                value={iconSize}
                                onChange={(e) => handleIconSizeChange(langCode, parseInt(e.target.value) || 60)}
                                style={{
                                  width: "100%",
                                  padding: "6px 10px",
                                  borderRadius: "6px",
                                  border: "2px solid #e2e8f0",
                                  fontSize: "12px",
                                  outline: "none",
                                  boxSizing: "border-box",
                                }}
                                onFocus={(e) => (e.target.style.borderColor = buttonColor)}
                                onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                              />
                            </div>
                          )}

                          {/* Action Buttons */}
                          {hasPreview ? (
                            // Show Upload and Cancel buttons when preview is shown
                            <div style={{ display: "flex", gap: "8px" }}>
                              <button
                                onClick={() => handleConfirmIconUpload(langCode)}
                                style={{
                                  flex: 1,
                                  padding: "8px 12px",
                                  backgroundColor: "#10b981",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "6px",
                                }}
                              >
                                <i className="fas fa-check"></i>
                                Upload
                              </button>
                              <button
                                onClick={() => handleCancelIconUpload(langCode)}
                                style={{
                                  padding: "8px 12px",
                                  backgroundColor: "#6b7280",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  cursor: "pointer",
                                  fontWeight: 600,
                                }}
                                title="Cancel"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ) : (
                            // Show Select/Change and Remove buttons when no preview
                            <div style={{ display: "flex", gap: "8px" }}>
                              <label style={{
                                flex: 1,
                                padding: "8px 12px",
                                backgroundColor: buttonColor,
                                color: "white",
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: 600,
                                cursor: "pointer",
                                textAlign: "center",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "6px",
                              }}>
                                <i className="fas fa-image"></i>
                                {hasCustomIcon ? "Change" : "Select"}
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleCustomIconUpload(e, langCode)}
                                  style={{ display: "none" }}
                                />
                              </label>

                              {hasCustomIcon && (
                                <button
                                  onClick={() => handleRemoveCustomIcon(langCode)}
                                  style={{
                                    padding: "8px 12px",
                                    backgroundColor: "#ef4444",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                  }}
                                  title="Remove custom icon"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Custom Display Names Section */}
          <div style={{
            marginTop: "20px",
            padding: "20px",
            backgroundColor: "#f8fafc",
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
          }}>
            <h4 style={{
              margin: "0 0 16px 0",
              color: "#2d3748",
              fontSize: "15px",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}>
              <i className="fas fa-edit" style={{ color: buttonColor }}></i> Custom Display Names
            </h4>
            <p style={{
              margin: "0 0 16px 0",
              fontSize: "12px",
              color: "#718096",
            }}>
              Customize the display name for each language. Leave empty to use the default native name.
            </p>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "15px",
            }}>
              {languages.map((langCode) => {
                const langInfo = getLanguageInfo(langCode);
                const customDisplayName = languageDisplayNames[langCode] || "";
                const defaultName = langInfo.name;

                return (
                  <div
                    key={langCode}
                    style={{
                      padding: "15px",
                      backgroundColor: "white",
                      borderRadius: "8px",
                      border: "2px solid #e2e8f0",
                    }}
                  >
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginBottom: "12px",
                    }}>
                      <span
                        className={`fi fi-${langInfo.flag}`}
                        style={{
                          fontSize: "24px",
                          borderRadius: "4px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#2d3748",
                        }}>
                          {langInfo.name}
                        </div>
                        <div style={{
                          fontSize: "11px",
                          color: "#718096",
                        }}>
                          {langCode.toUpperCase()}
                        </div>
                      </div>
                    </div>

                    <div style={{ marginBottom: "10px" }}>
                      <label style={{
                        display: "block",
                        fontSize: "11px",
                        color: "#718096",
                        marginBottom: "6px",
                        fontWeight: 600,
                      }}>
                        Display Name
                      </label>
                      <input
                        type="text"
                        value={customDisplayName}
                        placeholder={defaultName}
                        onChange={(e) => handleDisplayNameChange(langCode, e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          border: "2px solid #e2e8f0",
                          fontSize: "13px",
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                        onFocus={(e) => (e.target.style.borderColor = buttonColor)}
                        onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                      />
                      <div style={{
                        fontSize: "10px",
                        color: "#9ca3af",
                        marginTop: "4px",
                      }}>
                        Default: {defaultName}
                      </div>
                    </div>

                    <button
                      onClick={() => handleSaveDisplayName(langCode)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        backgroundColor: buttonColor,
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      <i className="fas fa-save"></i>
                      Save
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <SaveButton
            onClick={saveLanguageCustomization}
            loading={isSavingCustomization}
            loadingText="Saving Customization..."
            text="Save Customization"
            icon="fas fa-save"
          />
        </div>

        {/* Generate New Language */}
        <div
          style={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "12px",
            border: "2px dashed #e2e8f0",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-12px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: buttonColor,
              color: "white",
              padding: "6px 20px",
              borderRadius: "20px",
              fontSize: "14px",
              fontWeight: 600,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i className="fas fa-plus"></i> Add New Language
          </div>
          <div style={{ marginBottom: "25px", textAlign: "center" }}>
            <div
              style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                backgroundColor: `${buttonColor}20`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 15px",
                border: `2px solid ${buttonColor}`,
              }}
            >
              <i
                className="fas fa-language"
                style={{ fontSize: "24px", color: buttonColor }}
              ></i>
            </div>
            <h3
              style={{
                margin: "0 0 8px 0",
                color: "#2d3748",
                fontSize: "20px",
                fontWeight: 600,
              }}
            >
              Generate New Language
            </h3>
            <p style={{ margin: "0", color: "#718096", fontSize: "14px" }}>
              Select a language below to generate translations
            </p>
          </div>
          <div style={{ marginBottom: "20px", position: "relative" }}>
            <input
              type="text"
              placeholder="🔍 Search languages by name or code..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                width: "100%",
                padding: "14px 20px 14px 45px",
                borderRadius: "10px",
                border: "2px solid #e2e8f0",
                fontSize: "15px",
                outline: "none",
                transition: "all 0.2s",
                boxSizing: "border-box",
              }}
              onFocus={(e) => (e.target.style.borderColor = buttonColor)}
              onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
            />
          </div>
          {targetLanguage && (
            <div
              style={{
                marginBottom: "20px",
                padding: "18px",
                backgroundColor: `${buttonColor}10`,
                borderRadius: "10px",
                border: `2px solid ${buttonColor}`,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "15px",
                }}
              >
                <span
                  className={`fi fi-${getLanguageFlag(targetLanguage)}`}
                  style={{
                    fontSize: "28px",
                    borderRadius: "6px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                ></span>
                <div style={{ textAlign: "left" }}>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "#2d3748",
                    }}
                  >
                    {getLanguageName(targetLanguage)}
                  </div>
                  <div style={{ fontSize: "13px", color: "#718096" }}>
                    Ready to generate
                  </div>
                </div>
                <button
                  onClick={() => setTargetLanguage("")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#f56565",
                    cursor: "pointer",
                    fontSize: "22px",
                    padding: "4px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginLeft: "10px",
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          )}
          {!targetLanguage && filteredLanguages.length > 0 && (
            <div
              style={{
                maxHeight: "250px",
                overflowY: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                marginBottom: "20px",
                padding: "15px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                  gap: "12px",
                }}
              >
                {filteredLanguages.map((lang) => (
                  <div
                    key={lang.code}
                    onClick={() => setTargetLanguage(lang.code)}
                    style={{
                      padding: "15px",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      backgroundColor: "white",
                      cursor: "pointer",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "10px",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(0,0,0,0.1)";
                      e.currentTarget.style.borderColor = buttonColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                      e.currentTarget.style.borderColor = "#e2e8f0";
                    }}
                  >
                    <span
                      className={`fi fi-${lang.flag}`}
                      style={{
                        fontSize: "28px",
                        borderRadius: "6px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                      }}
                    ></span>
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#2d3748",
                          marginBottom: "4px",
                        }}
                      >
                        {lang.name}
                      </div>
                      <div style={{ fontSize: "12px", color: "#718096" }}>
                        {lang.code.toUpperCase()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!targetLanguage && searchQuery && filteredLanguages.length === 0 && (
            <div
              style={{
                padding: "30px 20px",
                textAlign: "center",
                color: "#718096",
                marginBottom: "20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px dashed #e2e8f0",
              }}
            >
              <i
                className="fas fa-search"
                style={{ fontSize: "36px", marginBottom: "12px", opacity: 0.5 }}
              ></i>
              <p style={{ margin: "0 0 8px 0", fontWeight: 500 }}>
                No languages found for "{searchQuery}"
              </p>
              <p style={{ margin: 0, fontSize: "13px" }}>
                Try a different search term
              </p>
            </div>
          )}
          <div style={{ marginTop: "25px" }}>
            <button
              onClick={handleGenerateLanguage}
              disabled={isGenerating || !targetLanguage}
              style={{
                width: "100%",
                padding: "16px",
                fontSize: "16px",
                fontWeight: 600,
                borderRadius: "10px",
                border: "none",
                backgroundColor: targetLanguage ? buttonColor : "#cbd5e0",
                color: "white",
                cursor: isGenerating ? "not-allowed" : "pointer",
                opacity: isGenerating ? 0.7 : 1,
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}
              onMouseEnter={(e) => {
                if (!isGenerating && targetLanguage) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = `0 6px 15px ${buttonColor}40`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isGenerating && targetLanguage) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }
              }}
            >
              {isGenerating ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Generating...
                </>
              ) : targetLanguage ? (
                <>
                  <i className="fas fa-bolt"></i> Generate{" "}
                  {getLanguageName(targetLanguage)}
                </>
              ) : (
                <>
                  <i className="fas fa-language"></i> Select a Language
                </>
              )}
            </button>
            <p
              style={{
                margin: "15px 0 0 0",
                color: "#718096",
                fontSize: "12px",
                textAlign: "center",
              }}
            >
              Generates translations for all system content • Media files copied
              from English • May take a few minutes
            </p>
          </div>
        </div>

        <div style={{ marginTop: "40px" }}>
          <VideoGenerationStatus userId={userId} buttonColor={buttonColor} />
        </div>
        <div style={{ marginTop: "40px" }}>
          <ScheduledJobsManager userId={userId} buttonColor={buttonColor} />
        </div>
      </div>

      <style jsx>{`
        @import url("https://cdn.jsdelivr.net/gh/lipis/flag-icons@6.6.6/css/flag-icons.min.css");
        @import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css");
        .fi {
          display: inline-block;
          background-size: cover;
        }
        ::-webkit-scrollbar {
          width: 6px;
        }
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
        .fa-spin {
          animation: fa-spin 1s linear infinite;
        }
        @keyframes fa-spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes slideDown {
          from {
            transform: translate(-50%, -100%);
            opacity: 0;
          }
          to {
            transform: translate(-50%, 0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  )
};

const getLanguageName = (code) => {
  const names = {
    en: "English",
    es: "Español",
    fr: "Français",
    de: "Deutsch",
    it: "Italiano",
    pt: "Português",
    ru: "Русский",
    zh: "中文",
    ja: "日本語",
    ko: "한국어",
    ar: "العربية",
    hi: "हिन्दी",
    tr: "Türkçe",
    nl: "Nederlands",
    sv: "Svenska",
    pl: "Polski",
    th: "ไทย",
    vi: "Tiếng Việt",
    id: "Bahasa Indonesia",
  };
  return names[code] || code.toUpperCase();
};

const getLanguageFlag = (code) => {
  const flags = {
    en: "us",
    es: "es",
    fr: "fr",
    de: "de",
    it: "it",
    pt: "pt",
    ru: "ru",
    zh: "cn",
    ja: "jp",
    ko: "kr",
    ar: "sa",
    hi: "in",
    tr: "tr",
    nl: "nl",
    sv: "se",
    pl: "pl",
    th: "th",
    vi: "vn",
    id: "id",
  };
  return flags[code] || "xx";
};

export default LanguageSelector;
